import React from 'react';
import {extractPlainTextFromContent} from '../extractEntitiesFromContent';
import {getDeltaTimelineStories} from '../../../../../utils/api/deltas';

function utcYmdFromIso(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
}

function todayUtcYmd() {
    return new Date().toISOString().slice(0, 10);
}

/**
 * Fetches POST /api/entities/delta-timeline for the baseline calendar day through today (UTC dates).
 * Server caches profile + chunk in entity_delta_cluster_cache; we pass profileKey from the last response
 * when inputs are unchanged so the API can validate the client is on the same FINTEL hash.
 */
export default function useDeltaTimeline({content, publishedAt, reportId, deltaMode, deltaRunEpoch = 0, enabled = true}) {
    const [stories, setStories] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [hasReportText, setHasReportText] = React.useState(false);

    const profileKeyRef = React.useRef(null);

    const reportText = React.useMemo(() => {
        if (!content) return '';
        return extractPlainTextFromContent(content);
    }, [content]);

    const fetchKey = React.useMemo(
        () => `${publishedAt ?? ''}|${reportId ?? ''}|${deltaMode ?? ''}|${deltaRunEpoch}|${reportText}`,
        [publishedAt, reportId, deltaMode, deltaRunEpoch, reportText]
    );

    React.useEffect(() => {
        profileKeyRef.current = null;
    }, [fetchKey]);

    React.useEffect(() => {
        if (!enabled) {
            setStories([]);
            setHasReportText(false);
            setError(null);
            setLoading(false);
            return undefined;
        }

        if (!content) {
            setStories([]);
            setHasReportText(false);
            setError(null);
            setLoading(false);
            return undefined;
        }

        setHasReportText(Boolean(reportText && reportText.trim()));

        if (!publishedAt || !reportText.trim()) {
            setStories([]);
            setError(null);
            setLoading(false);
            return undefined;
        }

        const windowStart = utcYmdFromIso(publishedAt);
        const windowEnd = todayUtcYmd();
        if (!windowStart) {
            setStories([]);
            setError(null);
            setLoading(false);
            return undefined;
        }

        setLoading(true);
        setError(null);

        let cancelled = false;

        getDeltaTimelineStories({
            baseline: publishedAt,
            reportText,
            windowStart,
            windowEnd,
            profileKey: profileKeyRef.current || undefined,
            reportId,
            deltaMode,
        })
            .then((result) => {
                if (cancelled) return;
                if (result?.profileKey) {
                    profileKeyRef.current = result.profileKey;
                }
                setStories(Array.isArray(result?.stories) ? result.stories : []);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('[DeltaPanel] Failed to fetch delta timeline:', err);
                setError('Failed to load timeline.');
                setStories([]);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [enabled, fetchKey, publishedAt, reportText, reportId, deltaMode, content]);

    return {
        stories,
        timelineLoading: loading,
        timelineError: error,
        hasReportText,
    };
}
